import { createAsyncThunk } from "@reduxjs/toolkit";
import { phpClient } from "../../../network";
import {
    GetSchedulesV3Params,
    GetSchedulesV3Response,
    buildScheduleV3Request,
    AddScheduleRequest,
    AddScheduleV2Request,
    UpdateScheduleRequest,
    DeleteScheduleV3Request,
    PublishScheduleV3Request,
    PublishOpenShiftRequest,
    GetPublishedShiftsForAssignRequest,
    GetPublishedShiftsForAssignResponse,
    AssignOpenShiftToEmployeeRequest,
    PhpResponse,
    CreateOpenShiftsRequest,
    CopyScheduleRequest,
    GetScheduleSidebarListParams,
    ScheduleSidebarListResponse,
    ScheduleAuditLogsRequest,
    ScheduleAuditLogsResponse,
} from "../../../types";

// Request type for updateScheduleV2 - flat structure with schedule_id for URL
export interface UpdateScheduleV2Request {
    schedule_id: string | number;
    os_emp_account_id: string;
    os_schedules_site_shifts_id?: string | number;
    schedule_date?: string;
    us_user_site_profile_id?: string;
    department_code?: string;
    os_work_codes_id?: string;
    shift_color?: string;
    logs?: string;
    shift_type?: 'template' | 'free_flow' | 'notes';
    free_flow_shift?: {
        start_datetime: string;
        end_datetime: string;
        title?: string;
    };
    shift_attributes?: {
        site_code?: string;
        department_code?: string;
        work_code?: string;
        notes?: string;
        logs?: string;
        shift_color?: string;
        attendance_tags?: Array<{
            code: string;
            value: string | string[];
        }>;
    };
}

// API endpoints
const SCHEDULE_V3_ENDPOINT = "/Schedule/my_schedules_v3";
const SCHEDULE_V3_ENDPOINT_V4 = "/Schedule/my_schedules_v4";
const ADD_SCHEDULE_ENDPOINT = "/Schedule/add_schedule";
const ADD_SCHEDULE_V2_ENDPOINT = "/Schedule/add_schedule_v2";
const UPDATE_SCHEDULE_ENDPOINT = "/schedule/update_schedule";
const UPDATE_SCHEDULE_V2_ENDPOINT = "/schedule/update_schedule";
const DELETE_SCHEDULE_ENDPOINT = "/schedule/delete_schedule";
const DELETE_SCHEDULE_V2_ENDPOINT = "/schedule/delete";
const PUBLISH_SCHEDULE_ENDPOINT = "/schedule/publish_schedule";
const PUBLISH_OPEN_SHIFT_ENDPOINT = "/Schedule/publish_open_shifts";
const GET_PUBLISHED_SHIFTS_FOR_ASSIGN_ENDPOINT = "/Schedule/get_published_shifts_for_assign";
const ASSIGN_OPEN_SHIFT_TO_EMPLOYEE_ENDPOINT = "/Schedule/assign_open_shift_to_employee";
const CREATE_OPEN_SHIFTS_ENDPOINT = "/schedule/add_open_shift";
const COPY_SCHEDULE_ENDPOINT = "/schedule/copy";
const SCHEDULE_SIDEBAR_LIST_ENDPOINT = "/schedule/sidebar_list_v4";
const SCHEDULE_AUDIT_LOGS_ENDPOINT = "/schedule/get_schedule_audit_logs";

export const getScheduleAuditLogs = async (request: ScheduleAuditLogsRequest): Promise<ScheduleAuditLogsResponse> => {
    return phpClient.post<ScheduleAuditLogsResponse>({
        url: SCHEDULE_AUDIT_LOGS_ENDPOINT,
        data: request,
    });
};

/**
 * Fetch schedules using the V3 API
 * @param params - Request parameters including date range, grouping, and filters
 * @returns Schedule V3 response with grouped data
 */
export const getSchedulesV3 = createAsyncThunk<GetSchedulesV3Response, GetSchedulesV3Params, { rejectValue: string }>(
    "scheduleV3/getSchedules",
    async (params, { rejectWithValue }) => {
        try {
            const requestBody = buildScheduleV3Request(params);

            const response = await phpClient.post<GetSchedulesV3Response>({
                // url: SCHEDULE_V3_ENDPOINT,
                url: SCHEDULE_V3_ENDPOINT_V4,
                data: requestBody,
            });

            if (!response.status) {
                return rejectWithValue(response.message || "Failed to fetch schedules");
            }

            return response;
        } catch (error: any) {
            console.error("Schedule V3 API Error:", error);

            if (error.response) {
                // Server responded with error
                return rejectWithValue(error.response.data?.message || `Server error: ${error.response.status}`);
            } else if (error.request) {
                // Request made but no response
                return rejectWithValue("No response from server. Please check your connection.");
            } else {
                // Error in request setup
                return rejectWithValue(error.message || "An unexpected error occurred");
            }
        }
    }
);

/**
 * Fetch sidebar layout (employees/departments) for Scheduling Tool.
 * This is used to drive sidebar + layout instead of client-side mapping.
 */
export const getScheduleSidebarList = createAsyncThunk<
    ScheduleSidebarListResponse,
    GetScheduleSidebarListParams,
    { rejectValue: string }
>("scheduleV3/getScheduleSidebarList", async (params, { rejectWithValue }) => {
    try {
        const response = await phpClient.post<ScheduleSidebarListResponse>({
            url: SCHEDULE_SIDEBAR_LIST_ENDPOINT,
            data: {
                start_date: params.start_date,
                end_date: params.end_date,
                group_by: params.group_by,
                ...(params.sort_by != null && { sort_by: params.sort_by }),
                filters: {
                    department_codes: params.filters?.department_codes ?? [],
                    employee_ids: params.filters?.employee_ids ?? [],
                    site_ids: params.filters?.site_ids ?? [],
                    tag_code: params.filters?.tag_code ?? null,
                    group_code: params.filters?.group_code ?? [],
                },
                include_mydept: params.include_mydept,
            },
        });

        // Even if status is false, we still want to use data to render layout.
        return response;
    } catch (error: any) {
        console.error("Schedule Sidebar API Error:", error);

        if (error.response) {
            return rejectWithValue(error.response.data?.message || `Server error: ${error.response.status}`);
        } else if (error.request) {
            return rejectWithValue("No response from server. Please check your connection.");
        } else {
            return rejectWithValue(error.message || "An unexpected error occurred");
        }
    }
});

/**
 * Refresh schedules - same as getSchedulesV3 but can be used for explicit refresh actions
 */
export const refreshSchedulesV3 = createAsyncThunk<
    GetSchedulesV3Response,
    GetSchedulesV3Params,
    { rejectValue: string }
>("scheduleV3/refreshSchedules", async (params, { rejectWithValue }) => {
    try {
        const requestBody = buildScheduleV3Request(params);

        const response = await phpClient.post<GetSchedulesV3Response>({
            // url: SCHEDULE_V3_ENDPOINT,
            url: SCHEDULE_V3_ENDPOINT_V4,
            data: requestBody,
        });

        if (!response.status) {
            return rejectWithValue(response.message || "Failed to refresh schedules");
        }

        return response;
    } catch (error: any) {
        console.error("Schedule V3 Refresh Error:", error);
        return rejectWithValue(error.message || "Failed to refresh schedules");
    }
});

/**
 * Add a new schedule (V1)
 * @param request - Schedule details including employee, shift, dates, etc.
 * @returns Response indicating success or failure
 */
export const addSchedule = createAsyncThunk<PhpResponse<any>, AddScheduleRequest, { rejectValue: string }>(
    "scheduleV3/addSchedule",
    async (request, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<any>>({
                url: ADD_SCHEDULE_ENDPOINT,
                data: request,
            });

            if (!response.status) {
                return rejectWithValue(response.message || "Failed to add schedule");
            }

            return response;
        } catch (error: any) {
            console.error("Add Schedule API Error:", error);

            if (error.response) {
                return rejectWithValue(error.response.data?.message || `Server error: ${error.response.status}`);
            } else if (error.request) {
                return rejectWithValue("No response from server. Please check your connection.");
            } else {
                return rejectWithValue(error.message || "An unexpected error occurred");
            }
        }
    }
);

/**
 * Add a new schedule (V2) - Enhanced with better assignment options
 * @param request - Schedule details including employee, shift, dates, etc.
 * @returns Response indicating success or failure with assigned count
 */
export const addScheduleV2 = createAsyncThunk<PhpResponse<any>, AddScheduleV2Request, { rejectValue: string }>(
    "scheduleV3/addScheduleV2",
    async (request, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<any>>({
                url: ADD_SCHEDULE_V2_ENDPOINT,
                data: request,
            });

            if (!response.status) {
                return rejectWithValue(response.message || "Failed to add schedule");
            }

            return response;
        } catch (error: any) {
            console.error("Add Schedule V2 API Error:", error);

            if (error.response) {
                return rejectWithValue(error.response.data?.message || `Server error: ${error.response.status}`);
            } else if (error.request) {
                return rejectWithValue("No response from server. Please check your connection.");
            } else {
                return rejectWithValue(error.message || "An unexpected error occurred");
            }
        }
    }
);

/**
 * Update an existing schedule
 * @param request - Schedule details to update including schedule ID
 * @returns Response indicating success or failure
 */
export const updateSchedule = createAsyncThunk<PhpResponse<any>, UpdateScheduleRequest, { rejectValue: string }>(
    "scheduleV3/updateSchedule",
    async (request, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<any>>({
                url: UPDATE_SCHEDULE_ENDPOINT,
                data: request,
            });

            if (!response.status) {
                return rejectWithValue(response.message || "Failed to update schedule");
            }

            return response;
        } catch (error: any) {
            console.error("Update Schedule API Error:", error);

            if (error.response) {
                return rejectWithValue(error.response.data?.message || `Server error: ${error.response.status}`);
            } else if (error.request) {
                return rejectWithValue("No response from server. Please check your connection.");
            } else {
                return rejectWithValue(error.message || "An unexpected error occurred");
            }
        }
    }
);

/**
 * Delete a schedule
 * @param request - Schedule and employee IDs to delete
 * @returns Response indicating success or failure
 */
export const deleteSchedule = createAsyncThunk<PhpResponse<any>, DeleteScheduleV3Request, { rejectValue: string }>(
    "scheduleV3/deleteSchedule",
    async (request, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<any>>({
                url: DELETE_SCHEDULE_ENDPOINT,
                data: {
                    os_emp_schedules_id: request.os_emp_schedules_id,
                    os_emp_account_id: request.os_emp_account_id,
                },
            });

            if (!response.status) {
                return rejectWithValue(response.message || "Failed to delete schedule");
            }

            return response;
        } catch (error: any) {
            console.error("Delete Schedule API Error:", error);

            if (error.response) {
                return rejectWithValue(error.response.data?.message || `Server error: ${error.response.status}`);
            } else if (error.request) {
                return rejectWithValue("No response from server. Please check your connection.");
            } else {
                return rejectWithValue(error.message || "An unexpected error occurred");
            }
        }
    }
);

/**
 * Update an existing schedule (V2) - Enhanced with better assignment options
 * @param request - Schedule details to update including schedule ID
 * @returns Response indicating success or failure with assigned count
 */
export const updateScheduleV2 = createAsyncThunk<PhpResponse<any>, UpdateScheduleV2Request, { rejectValue: string }>(
    "scheduleV3/updateScheduleV2",
    async (request, { rejectWithValue }) => {
        try {
            const { schedule_id, ...updateData } = request;

            // Remove 'notes' if present and ensure only 'logs' is sent
            const updateDataAny = updateData as any;
            const { notes, ...dataWithoutNotes } = updateDataAny;
            
            // Also handle shift_attributes.notes -> shift_attributes.logs if present
            if (dataWithoutNotes.shift_attributes && (dataWithoutNotes.shift_attributes as any).notes) {
                const shiftAttrs = dataWithoutNotes.shift_attributes as any;
                const { notes: shiftNotes, ...shiftAttrsWithoutNotes } = shiftAttrs;
                dataWithoutNotes.shift_attributes = {
                    ...shiftAttrsWithoutNotes,
                    logs: shiftNotes || shiftAttrs.logs
                };
            }
            
            const req = { 
                ...dataWithoutNotes,
                logs: updateData.logs
            }
            const response = await phpClient.post<PhpResponse<any>>({
                url: `${UPDATE_SCHEDULE_V2_ENDPOINT}/${schedule_id}`,
                data: req,
            });

            if (!response.status) {
                return rejectWithValue(response.message || "Failed to update schedule");
            }

            return response;
        } catch (error: any) {
            console.error("Update Schedule V2 API Error:", error);

            if (error.response) {
                return rejectWithValue(error.response.data?.message || `Server error: ${error.response.status}`);
            } else if (error.request) {
                return rejectWithValue("No response from server. Please check your connection.");
            } else {
                return rejectWithValue(error.message || "An unexpected error occurred");
            }
        }
    }
);

/**
 * Delete a schedule (V2)
 * @param scheduleId - Schedule ID to delete
 * @returns Response indicating success or failure
 */
export const deleteScheduleV2 = createAsyncThunk<PhpResponse<any>, number, { rejectValue: string }>(
    "scheduleV3/deleteScheduleV2",
    async (scheduleId, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<any>>({
                // url: `${DELETE_SCHEDULE_V2_ENDPOINT}/${scheduleId}`,
                url: `${DELETE_SCHEDULE_ENDPOINT}/${scheduleId}`,
                data: {},
            });

            if (!response.status) {
                return rejectWithValue(response.message || "Failed to delete schedule");
            }

            return response;
        } catch (error: any) {
            console.error("Delete Schedule V2 API Error:", error);

            if (error.response) {
                return rejectWithValue(error.response.data?.message || `Server error: ${error.response.status}`);
            } else if (error.request) {
                return rejectWithValue("No response from server. Please check your connection.");
            } else {
                return rejectWithValue(error.message || "An unexpected error occurred");
            }
        }
    }
);

/**
 * Publish schedules for a date range
 * @param request - Publish parameters: date range, scope, options
 * @returns Response indicating success or failure
 */
export const publishSchedule = createAsyncThunk<PhpResponse<any>, PublishScheduleV3Request, { rejectValue: string }>(
    "scheduleV3/publishSchedule",
    async (request, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<any>>({
                url: PUBLISH_SCHEDULE_ENDPOINT,
                data: {
                    start_date: request.start_date,
                    end_date: request.end_date,
                    scope: request.scope,
                    employee_ids: request.employee_ids ?? [],
                    department_codes: request.department_codes ?? [],
                    site_ids: request.site_ids ?? [],
                    publish_note: request.publish_note ?? "",
                    send_notification: request.send_notification,
                    generate_ics: request.generate_ics,
                },
            });

            console.log("Publish Schedule API Response:", response);

            if (!response.status) {
                return rejectWithValue(response.message || "Failed to publish schedule");
            }

            return response;
        } catch (error: any) {
            console.error("Publish Schedule API Error:", error);

            if (error.response) {
                return rejectWithValue(error.response.data?.message || `Server error: ${error.response.status}`);
            } else if (error.request) {
                return rejectWithValue("No response from server. Please check your connection.");
            } else {
                return rejectWithValue(error.message || "An unexpected error occurred");
            }
        }
    }
);

/**
 * Create open shifts
 * @param request - Open shift details including shifts array, employee IDs, department codes, and group codes
 * @returns Response indicating success or failure
 */
export const createOpenShifts = createAsyncThunk<PhpResponse<any>, CreateOpenShiftsRequest, { rejectValue: string }>(
    "scheduleV3/createOpenShifts",
    async (request, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<any>>({
                url: CREATE_OPEN_SHIFTS_ENDPOINT,
                data: request,
            });

            if (!response.status) {
                return rejectWithValue(response.message || "Failed to create open shifts");
            }

            return response;
        } catch (error: any) {
            console.error("Create Open Shifts API Error:", error);

            if (error.response) {
                return rejectWithValue(error.response.data?.message || `Server error: ${error.response.status}`);
            } else if (error.request) {
                return rejectWithValue("No response from server. Please check your connection.");
            } else {
                return rejectWithValue(error.message || "An unexpected error occurred");
            }
        }
    }
);

/**
 * Publish open shifts for a date range
 * @param request - Start and end dates for publishing open shifts
 * @returns Response indicating success or failure
 */
export const publishOpenShift = createAsyncThunk<PhpResponse<any>, PublishOpenShiftRequest, { rejectValue: string }>(
    "scheduleV3/publishOpenShift",
    async (request, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<any>>({
                url: PUBLISH_OPEN_SHIFT_ENDPOINT,
                data: {
                    start_date: request.start_date,
                    end_date: request.end_date,
                },
            });

            console.log("Publish Open Shift API Response:", response);

            if (!response.status) {
                return rejectWithValue(response.message || "Failed to publish open shift");
            }

            return response;
        } catch (error: any) {
            console.error("Publish Open Shift API Error:", error);

            if (error.response) {
                return rejectWithValue(error.response.data?.message || `Server error: ${error.response.status}`);
            } else if (error.request) {
                return rejectWithValue("No response from server. Please check your connection.");
            } else {
                return rejectWithValue(error.message || "An unexpected error occurred");
            }
        }
    }
);

/**
 * Get published shifts for assignment to employees
 * @param request - Start and end dates for fetching published shifts
 * @returns Response with published shifts and eligible employees
 */
export const getPublishedShiftsForAssign = createAsyncThunk<
    GetPublishedShiftsForAssignResponse,
    GetPublishedShiftsForAssignRequest,
    { rejectValue: string }
>("scheduleV3/getPublishedShiftsForAssign", async (request, { rejectWithValue }) => {
    try {
        const response = await phpClient.post<GetPublishedShiftsForAssignResponse>({
            url: GET_PUBLISHED_SHIFTS_FOR_ASSIGN_ENDPOINT,
            data: {
                start_date: request.start_date,
                end_date: request.end_date,
            },
        });

        if (!response.status) {
            return rejectWithValue(response.message || "Failed to fetch published shifts for assignment");
        }

        return response;
    } catch (error: any) {
        console.error("Get Published Shifts for Assignment API Error:", error);

        if (error.response) {
            return rejectWithValue(error.response.data?.message || `Server error: ${error.response.status}`);
        } else if (error.request) {
            return rejectWithValue("No response from server. Please check your connection.");
        } else {
            return rejectWithValue(error.message || "An unexpected error occurred");
        }
    }
});

/**
 * Assign open shifts to employees
 * @param request - Array of shift assignments containing shift IDs and employee IDs
 * @returns Response indicating success or failure
 */
export const assignOpenShiftToEmployee = createAsyncThunk<
    PhpResponse<any>,
    AssignOpenShiftToEmployeeRequest,
    { rejectValue: string }
>("scheduleV3/assignOpenShiftToEmployee", async (request, { rejectWithValue }) => {
    try {
        const response = await phpClient.post<PhpResponse<any>>({
            url: ASSIGN_OPEN_SHIFT_TO_EMPLOYEE_ENDPOINT,
            data: request,
        });

        if (!response.status) {
            return rejectWithValue(response.message || "Failed to assign open shift to employee");
        }

        return response;
    } catch (error: any) {
        console.error("Assign Open Shift to Employee API Error:", error);

        if (error.response) {
            return rejectWithValue(error.response.data?.message || `Server error: ${error.response.status}`);
        } else if (error.request) {
            return rejectWithValue("No response from server. Please check your connection.");
        } else {
            return rejectWithValue(error.message || "An unexpected error occurred");
        }
    }
});

/**
 * Copy schedules from source date range to destination date
 * @param request - Source and destination dates with optional filters
 * @returns Response indicating success or failure with count of copied shifts
 */
export const copySchedule = createAsyncThunk<PhpResponse<any>, CopyScheduleRequest, { rejectValue: string }>(
    "scheduleV3/copySchedule",
    async (request, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<any>>({
                url: COPY_SCHEDULE_ENDPOINT,
                data: request,
            });

            if (!response.status) {
                return rejectWithValue(response.message || "Failed to copy schedule");
            }

            return response;
        } catch (error: any) {
            console.error("Copy Schedule API Error:", error);

            if (error.response) {
                return rejectWithValue(error.response.data?.message || `Server error: ${error.response.status}`);
            } else if (error.request) {
                return rejectWithValue("No response from server. Please check your connection.");
            } else {
                return rejectWithValue(error.message || "An unexpected error occurred");
            }
        }
    }
);

// Feature flags
export const USE_ADD_SCHEDULE_V2 = true;
export const USE_UPDATE_SCHEDULE_V2 = true;
export const USE_DELETE_SCHEDULE_V2 = true;

// Export API functions
export const scheduleV3Api = {
    getSchedules: getSchedulesV3,
    refreshSchedules: refreshSchedulesV3,
    addSchedule: addSchedule,
    addScheduleV2: addScheduleV2,
    updateSchedule: updateSchedule,
    updateScheduleV2: updateScheduleV2,
    deleteSchedule: deleteSchedule,
    deleteScheduleV2: deleteScheduleV2,
    publishSchedule: publishSchedule,
    createOpenShifts: createOpenShifts,
    publishOpenShift: publishOpenShift,
    getPublishedShiftsForAssign: getPublishedShiftsForAssign,
    assignOpenShiftToEmployee: assignOpenShiftToEmployee,
    copySchedule: copySchedule,
    getScheduleAuditLogs: getScheduleAuditLogs,
};
